
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeHtml } from "@/utils/sanitization";

interface BannerData {
  id: string;
  title: string | null;
  content: string | null;
  banner_type: 'text' | 'image';
  image_url: string | null;
  background_color: string;
  text_color: string;
  is_active: boolean;
}

const Banner = () => {
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const location = useLocation();

  // Only show banner on home page
  const shouldShowBanner = location.pathname === '/';

  useEffect(() => {
    if (shouldShowBanner) {
      fetchActiveBanner();
    }
  }, [shouldShowBanner]);

  const fetchActiveBanner = async () => {
    try {
      const { data, error } = await supabase
        .from('banners')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching banner:', error);
        return;
      }

      if (data && data.length > 0) {
        setBanner(data[0]);
      }
    } catch (error) {
      console.error('Error fetching banner:', error);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!banner || !isVisible || !shouldShowBanner) {
    return null;
  }

  return (
    <div 
      className="relative w-full py-2 px-4 flex items-center justify-center"
      style={{ 
        backgroundColor: banner.background_color || '#f3f4f6',
        color: banner.text_color || '#374151'
      }}
    >
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex-1 flex items-center justify-center">
          {banner.banner_type === 'image' && banner.image_url ? (
            <div className="flex items-center gap-3">
              <img 
                src={banner.image_url} 
                alt={banner.title || 'Banner'} 
                className="h-8 w-auto object-contain"
              />
              {banner.title && (
                <span className="font-medium text-sm">{banner.title}</span>
              )}
            </div>
          ) : (
            <div className="text-center leading-tight">
              {banner.title && (
                <h3 className="font-bold text-sm mb-0.5 leading-tight">{banner.title}</h3>
              )}
              {banner.content && (
                <div 
                  className="text-sm leading-tight [&_p]:m-0 [&_p]:leading-tight [&_h1]:m-0 [&_h1]:leading-tight [&_h2]:m-0 [&_h2]:leading-tight [&_h3]:m-0 [&_h3]:leading-tight [&_h4]:m-0 [&_h4]:leading-tight" 
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(banner.content) }}
                />
              )}
            </div>
          )}
        </div>
        
        <button
          onClick={handleClose}
          className="ml-4 p-1 hover:opacity-70 transition-opacity flex-shrink-0"
          aria-label="Close banner"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default Banner;
